import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { PROMO } from "../../lib/promo";
import { PROMO_IDS } from "../../constants/testIds";
import { trackEvent } from "../../lib/track";
import { BOOKING_URL } from "../../lib/site";

const STORAGE_KEY = "alae-promo-modal-seen";
const COOKIE_KEY = "alae-cookie-consent";
const EASE = [0.22, 1, 0.36, 1];

export default function PromoModal() {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  const close = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!PROMO.enabled) return;
    if (pathname !== "/") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let timer;
    // Never interrupt someone who is already filling in a form.
    const onFormFocus = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        sessionStorage.setItem(STORAGE_KEY, "1");
        clearTimeout(timer);
        setVisible(false);
      }
    };
    document.addEventListener("focusin", onFormFocus);

    const arm = (delay) => {
      timer = setTimeout(() => {
        const active = document.activeElement?.tagName;
        if (active === "INPUT" || active === "TEXTAREA" || active === "SELECT") return;
        setVisible(true);
      }, delay);
    };

    let cleanupConsent = () => {};
    if (localStorage.getItem(COOKIE_KEY)) {
      arm(5000);
    } else {
      const onConsent = () => arm(1200);
      window.addEventListener("alae:cookie-consent", onConsent, { once: true });
      cleanupConsent = () =>
        window.removeEventListener("alae:cookie-consent", onConsent);
    }

    return () => {
      document.removeEventListener("focusin", onFormFocus);
      cleanupConsent();
      clearTimeout(timer);
    };
  }, [pathname]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);

    // Lock the page behind the modal. Lenis drives the scroll, so pause it too.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      window.__lenis?.start();
    };
  }, [visible, close]);

  if (!PROMO.enabled) return null;

  return (
    <AnimatePresence>
      {visible && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Grand opening offers"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            onClick={close}
            className="absolute inset-0 bg-umber/50 backdrop-blur-[3px]"
          />

          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.99 }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid={PROMO_IDS.modal}
            className="relative w-full max-w-xl bg-paper border border-umber/20 shadow-paper paper-grain max-h-[90svh] overflow-hidden flex flex-col"
          >
            <button
              type="button"
              aria-label="Close grand opening offers"
              data-testid={PROMO_IDS.modalClose}
              onClick={close}
              className="absolute top-3 right-3 z-10 text-umber/70 hover:text-forest transition-colors p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-full bg-paper/70 backdrop-blur-sm border border-umber/15"
            >
              <X size={18} strokeWidth={1.6} />
            </button>

            {/* data-lenis-prevent: Lenis swallows wheel events while stopped,
                which would kill scrolling inside the modal. */}
            <div
              data-lenis-prevent
              className="relative overflow-y-auto overscroll-contain"
            >
              {/* Image band with logo */}
              <div className="relative h-36 sm:h-44 md:h-52 overflow-hidden">
                <img
                  src={PROMO.image}
                  alt={PROMO.imageAlt}
                  className="absolute inset-0 w-full h-full object-cover scale-x-[-1]"
                  style={{ objectPosition: "center 25%" }}
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-r from-paper via-paper/75 to-transparent"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-paper to-transparent"
                />
                <div className="absolute left-7 md:left-10 top-6 md:top-7">
                  <Logo size="sm" className="h-10 md:h-12" />
                </div>
                <div className="absolute left-7 md:left-10 bottom-5 md:bottom-6 right-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-[1px] w-8 bg-forest" />
                    <span className="font-meta text-forest">
                      {PROMO.popupEyebrow}
                    </span>
                  </div>
                  <h2 className="font-display text-3xl md:text-[40px] leading-[1.1] tracking-tight text-umber max-w-[80%]">
                    We're now{" "}
                    <span className="font-display-italic text-forest">
                      open.
                    </span>
                  </h2>
                </div>
              </div>

              <div className="px-7 md:px-10 pb-7 md:pb-9 pt-1">
                <div className="divide-y divide-umber/15">
                  <Link
                    to={`${PROMO.newPatientPath}?from=popup`}
                    data-testid={PROMO_IDS.modalNewPatient}
                    onClick={() => {
                      trackEvent("offer_click", {
                        offer: PROMO.newPatient.id,
                        placement: "popup",
                      });
                      close();
                    }}
                    className="group py-5 grid grid-cols-[minmax(96px,34%)_1fr] gap-5 md:gap-7 items-start"
                  >
                    <div className="font-display text-xl md:text-2xl leading-tight text-forest text-center pt-1">
                      Welcome
                      <br />
                      gift
                    </div>
                    <div className="border-l border-umber/20 pl-5 md:pl-7">
                      <div className="font-display text-lg md:text-xl text-umber leading-snug group-hover:text-forest transition-colors">
                        {PROMO.newPatient.title}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-umber/75">
                        {PROMO.newPatient.giftLine}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-forest">
                        See the details
                        <ArrowUpRight
                          size={13}
                          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                      </span>
                    </div>
                  </Link>

                  {PROMO.offers.map((o) => (
                    <div
                      key={o.id}
                      className="py-5 grid grid-cols-[minmax(96px,34%)_1fr] gap-5 md:gap-7 items-start"
                    >
                      <div className="font-display text-3xl md:text-[40px] leading-none text-umber text-center pt-1">
                        {o.price}
                      </div>
                      <div className="border-l border-umber/20 pl-5 md:pl-7">
                        <div className="font-display text-lg md:text-xl text-umber leading-snug">
                          {o.name}
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-umber/75">
                          {o.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pinned action bar. Lives outside the scroll area so the primary
                CTA is always reachable, including on short mobile screens. */}
            <div className="shrink-0 border-t border-umber/15 bg-paper px-7 md:px-10 pt-5 pb-6 md:pb-7">
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={PROMO_IDS.modalBook}
                  onClick={() => {
                    trackEvent("book_click", { placement: "popup" });
                    close();
                  }}
                  className="flex-1 bg-forest text-paper rounded-full px-7 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 text-center min-h-[44px] inline-flex items-center justify-center"
                >
                  Book Online
                </a>
                <Link
                  to={`${PROMO.path}?from=popup`}
                  data-testid={PROMO_IDS.modalDetails}
                  onClick={close}
                  className="group flex-1 inline-flex items-center justify-center gap-2 border border-umber/30 text-umber rounded-full px-7 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-colors duration-300 min-h-[44px]"
                >
                  See the details
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
              </div>
              <p className="mt-4 text-[11px] leading-relaxed text-umber/70 text-center">
                {PROMO.finePrint}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
